import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Text } from '@/components/TextComponent';
import { Box } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { DesktopSearchModal, DesktopSearchSection } from '@/feature/desktop-nav/components/DesktopSearchModal';
import {
    DesktopSearchResultRow,
    DesktopSearchResultRowSkeleton,
} from '@/feature/desktop-nav/components/DesktopSearchResultRow';

import { useCasinoDesktopSearch } from '../hooks/use-casino-desktop-search';
import { useCasinoGameInfo } from '../hooks/use-casino-game-info';
import { CasinoGame } from '../hooks/use-casino-games';
import { useStartCasinoGameUrl } from '../hooks/use-formatted-casino-game-url';

const SKELETON_ROW_COUNT = 8;

type GameRowProps = {
    game: CasinoGame;
    onSelect: () => void;
};

const GameRow = ({ game, onSelect }: GameRowProps) => {
    const { gameInfoText } = useCasinoGameInfo(game);
    const { handleStartGame } = useStartCasinoGameUrl(game);
    const image = game.thumbnail || game.logo;

    const onPress = () => {
        onSelect();
        handleStartGame();
    };

    return (
        <DesktopSearchResultRow
            title={game.name}
            subtitle={gameInfoText}
            imageUrl={image ? `https:${image}` : undefined}
            onPress={onPress}
            testID={`casino-search-row-${game.id}`}
        />
    );
};

type CasinoDesktopSearchModalProps = {
    visible: boolean;
    onClose: () => void;
};

export const CasinoDesktopSearchModal = ({ visible, onClose }: CasinoDesktopSearchModalProps) => {
    const { t } = useTranslation('casino');
    const [query, setQuery] = useState('');
    const {
        searchQuery,
        results,
        recentGames,
        showSkeleton,
        showNoResults,
        showRecent,
        hasNextPage,
        isFetchingNextPage,
        fetchNextPage,
    } = useCasinoDesktopSearch(query);

    const handleClose = () => {
        setQuery('');
        onClose();
    };

    const handleEndReached = () => {
        if (hasNextPage && !isFetchingNextPage) {
            fetchNextPage({ cancelRefetch: false });
        }
    };

    return (
        <DesktopSearchModal
            visible={visible}
            onClose={handleClose}
            query={query}
            onChangeQuery={setQuery}
            placeholder={t('search_placeholder')}
            closeAccessibilityLabel={t('close_search')}
            resetScrollKey={searchQuery}
            onEndReached={handleEndReached}
        >
            {showSkeleton ? (
                <DesktopSearchSection label={t('games')}>
                    {Array.from({ length: SKELETON_ROW_COUNT }, (_, index) => (
                        <DesktopSearchResultRowSkeleton key={index} />
                    ))}
                </DesktopSearchSection>
            ) : null}
            {results.length > 0 ? (
                <DesktopSearchSection label={t('games')}>
                    {results.map(game => (
                        <GameRow key={game.id} game={game} onSelect={handleClose} />
                    ))}
                </DesktopSearchSection>
            ) : null}
            {showNoResults ? (
                <Box px="s16" pb="s16">
                    <Text variant="bodySmall" color={ds.colorToken.text.secondary}>
                        {t('search_no_results', { query: searchQuery })}
                    </Text>
                </Box>
            ) : null}
            {showRecent ? (
                <DesktopSearchSection label={t('recent')}>
                    {recentGames.map(game => (
                        <GameRow key={game.id} game={game} onSelect={handleClose} />
                    ))}
                </DesktopSearchSection>
            ) : null}
        </DesktopSearchModal>
    );
};
